// useTeams — the band-scoped Teams (linked partner orgs) hook, mirroring useContacts: built
// on useBandQuery/useBandMutation so the query key is ALWAYS ['band', bandId, 'teams'] (the tenant in the key,
// the structural fix). The artist-side surface links a band to Label/Management/Booking orgs.
//
// The link TYPE is the org's own kind (derived, never band-declared — the change-type mutation is
// retired; PO 2026-08-20): disconnect drops assignments, stops future sharing, keeps past data. DROP-16: NO ghost-org
// auto-create — a lookup miss is a designed "no organization found" state (404 → the modal renders it), not a
// create. Every mutation surfaces the server error verbatim and NEVER reports success on an unpersisted write.
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type TeamsRead = components["schemas"]["TeamsRead"];
type TeamView = components["schemas"]["TeamView"];
type OrgLookupView = components["schemas"]["OrgLookupView"];
type InviteIssued = components["schemas"]["InviteIssued"];
type LinkCodeIssued = components["schemas"]["LinkCodeIssued"];
type LinkRedeemed = components["schemas"]["LinkRedeemed"];

export type { TeamsRead, TeamView, OrgLookupView, InviteIssued, LinkCodeIssued, LinkRedeemed };

/**
 * The link-type taxonomy — the SAME label|management|booking vocabulary as `org_type`, but carried on
 * the per-relationship `core.org_band_links.link_type` column. Presentation vocabulary only; the
 * server validates. Reuse `orgTypeLabel` (useOrg.ts) for the display label.
 */
export const LINK_TYPES = ["label", "management", "booking"] as const;
export type LinkType = (typeof LINK_TYPES)[number];

interface TeamsSnapshot {
  previous?: TeamsRead;
}

function rollback(qc: QueryClient, key: readonly unknown[], previous: TeamsRead | undefined): void {
  if (previous) qc.setQueryData(key, previous);
  else void qc.invalidateQueries({ queryKey: key });
}

// ---- query --------------------------------------------------------------------------------------------

/** `GET /bands/{bandId}/teams` — the band's linked partner orgs. A 403 surfaces as the query error.*/
export function useTeams() {
  const { api } = useApp();
  return useBandQuery<TeamsRead>(
    ["teams"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/teams", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

// ---- mutations ----------------------------------------------------------------------------------------

/**
 * `POST /bands/{bandId}/teams/lookup` ({email}) → OrgLookupView — the invite handshake's account-photo
 * confirm step. A 404 (no org for that email) throws ApiError(404); the modal renders the designed
 * "No organization found" state (DROP-16 — never a ghost-org create). Not optimistic (a pure lookup).
 */
export function useLookupOrg() {
  const { api } = useApp();
  return useBandMutation<OrgLookupView, string>(async (bandId, email) => {
    const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/teams/lookup", {
      params: { path: { bandId } },
      body: { email },
    });
    if (!response.ok || !data) throw new ApiError(response.status, error);
    return data;
  });
}

/**
 * `POST /bands/{bandId}/teams/link-code` → the one-time XXXX-XXXX code ("Link a team", PO 2026-08-20).
 * The label/manager/booking agent redeems it from their roster shell and the link goes live instantly.
 * No error toast — the modal owns the inline error.
 */
export function useGenerateLinkCode() {
  const { api } = useApp();
  return useBandMutation<LinkCodeIssued, void>(async (band) => {
    const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/teams/link-code", {
      params: { path: { bandId: band } },
    });
    if (!response.ok || !data) throw new ApiError(response.status, error);
    return data;
  });
}

/**
 * `POST /bands/{bandId}/teams/link-code/redeem` ({code}) — the code a TEAM generated for this band.
 * Success invalidates the teams key (the new link appears in Your Teams immediately). Errors surface
 * inline in the modal, verbatim.
 */
export function useRedeemLinkCode() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = ["band", bandId, "teams"] as const;

  return useBandMutation<LinkRedeemed, string>(
    async (band, code) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/teams/link-code/redeem", {
        params: { path: { bandId: band } },
        body: { code },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    },
  );
}

/** `POST /bands/{bandId}/teams/invites` ({email, linkType}) → InviteIssued. Invalidates the teams key.*/
export function useInviteOrg() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = ["band", bandId, "teams"] as const;

  return useBandMutation<InviteIssued, { email: string; linkType: string }>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/teams/invites", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
      onError: (err) => {
        pushToast(errorMessage(err, "Couldn't send the invite"));
      },
    },
  );
}

/**
 * `DELETE /bands/{bandId}/teams/{targetOrgId}` → 204 (disconnect). Optimistic remove with rollback+
 * toast; the server cascade drops assignments, stops future sharing, keeps past data.
 */
export function useDisconnectOrg() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = ["band", bandId, "teams"] as const;

  return useBandMutation<void, string, TeamsSnapshot>(
    async (band, targetOrgId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/teams/{targetOrgId}", {
        params: { path: { bandId: band, targetOrgId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onMutate: async (targetOrgId) => {
        await qc.cancelQueries({ queryKey: key });
        const previous = qc.getQueryData<TeamsRead>(key);
        qc.setQueryData<TeamsRead>(key, (old) =>
          old ? { items: (old.items ?? []).filter((t) => t.orgId !== targetOrgId) } : old,
        );
        return { previous };
      },
      onError: (err, _id, ctx) => {
        rollback(qc, key, ctx?.previous);
        pushToast(errorMessage(err, "Couldn't remove the team"));
      },
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    },
  );
}
